import {useCallback, useMemo} from 'react';

import type {StatusSegment} from '../constants';
import {useTeacherAssignmentStatusCountsQuery} from '../queries/TeacherAssignments.queries.generated';
import type {TeacherAssignmentsFilters} from './useTeacherAssignments';
import {buildAssignmentFilter} from './useTeacherAssignments';

export type AssignmentStatusCounts = Record<StatusSegment, number>;

/** The list's filter state minus segment (each alias supplies its own) and sort (irrelevant). */
export type AssignmentStatusCountsFilters = Omit<TeacherAssignmentsFilters, 'segment' | 'sort'>;

interface UseAssignmentStatusCountsResult {
  /** Per-status totals for the current scope + filters; undefined until the first response. */
  counts?: AssignmentStatusCounts;
  /** A (re)fetch is in flight — while true, `counts` may hold the previous variables' totals. */
  isLoading: boolean;
  hasError: boolean;
  /** Re-pull the totals when an action changes them (e.g. a delete). */
  refetchCounts: () => void;
}

export const useAssignmentStatusCounts = (
  filters: AssignmentStatusCountsFilters
): UseAssignmentStatusCountsResult => {
  const {
    search,
    createdBy,
    actorTeacherId,
    classroomId,
    asEducatorId,
    assignmentType,
    dueDateStart,
    dueDateEnd
  } = filters;

  const variables = useMemo(() => {
    const listFilters = {
      search,
      createdBy,
      actorTeacherId,
      classroomId,
      assignmentType,
      dueDateStart,
      dueDateEnd
    };
    // The builder drops the creator/type/date filters for drafts itself, so every count matches
    // exactly what clicking its tab shows.
    return {
      openFilter: buildAssignmentFilter({...listFilters, segment: 'open'}),
      closedFilter: buildAssignmentFilter({...listFilters, segment: 'closed'}),
      scheduledFilter: buildAssignmentFilter({...listFilters, segment: 'scheduled'}),
      draftFilter: buildAssignmentFilter({...listFilters, segment: 'draft'}),
      asEducatorId
    };
  }, [
    search,
    createdBy,
    actorTeacherId,
    classroomId,
    asEducatorId,
    assignmentType,
    dueDateStart,
    dueDateEnd
  ]);

  const {data, previousData, loading, error, refetch} = useTeacherAssignmentStatusCountsQuery({
    fetchPolicy: 'cache-and-network',
    variables
  });

  // Previous totals hold the labels steady while a filter change refetches (no flicker to plain).
  const page = data ?? previousData;
  const counts = useMemo(
    () =>
      page && {
        open: page.open?.count ?? 0,
        closed: page.closed?.count ?? 0,
        scheduled: page.scheduled?.count ?? 0,
        draft: page.draft?.count ?? 0
      },
    [page]
  );

  const refetchCounts = useCallback(() => {
    // Counts are decorative — a failed refresh just keeps the previous labels (no error UI).
    void refetch().catch(() => {});
  }, [refetch]);

  return {counts, isLoading: loading, hasError: error !== undefined, refetchCounts};
};
